import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Button,
  Spinner,
} from '@chakra-ui/react';

import { FaTrash, FaDownload } from 'react-icons/fa';

import { formatDateTime } from 'utils/date.util';

import { useColors } from './colors';

import {
  deleteInventoryApi,
  getInventoriesOverviewApi,
  downloadInventoryApi,
} from 'features/inventories/inventories.api';

const InventoriesTable = ({ inventories, onRefresh }) => {
  const dispatch = useDispatch();
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const { borderColor, textColor } = useColors();

  const getStatusColor = (status) => (status ? 'green.500' : 'red.500');

  const handleDelete = async (id) => {
    setDeletingId(id);
    const result = await dispatch(deleteInventoryApi(id));

    if (deleteInventoryApi.fulfilled.match(result)) {
      dispatch(getInventoriesOverviewApi());
      onRefresh();
    }

    setDeletingId(null);
  };

  const handleDownload = async (id, fileName) => {
    setDownloadingId(id);
    await dispatch(downloadInventoryApi({ id, fileName }));
    setDownloadingId(null);
  };

  return (
    <Table variant="simple" size="md" style={{ tableLayout: 'fixed' }}>
      <Thead>
        <Tr>
          <Th color={textColor} width="18%" maxWidth="18%">
            File Name
          </Th>
          <Th color={textColor} width="20%" maxWidth="20%">
            ID
          </Th>
          <Th color={textColor}>Supplier</Th>
          <Th color={textColor}>Source</Th>
          <Th color={textColor}>Upload Time</Th>
          <Th color={textColor}>Chunked</Th>
          <Th color={textColor}>Synced</Th>
          <Th color={textColor}>Action</Th>
        </Tr>
      </Thead>
      <Tbody>
        {inventories.map((item) => (
          <Tr key={item._id} borderBottom="1px solid" borderColor={borderColor}>
            <Td
              color={textColor}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {item.fileName}
            </Td>
            <Td color={textColor}>{item._id}</Td>
            <Td color={textColor}>{item.supplier.name}</Td>
            <Td color={textColor}>{item.sourceType}</Td>
            <Td color={textColor}>{formatDateTime(item.createdAt)}</Td>
            <Td>
              <Button size="sm" bg={getStatusColor(item.isChunked)} color="white">
                {item.isChunked ? 'Chunked' : 'Not Chunked'}
              </Button>
            </Td>
            <Td>
              <Button size="sm" bg={getStatusColor(item.isProcessed)} color="white">
                {item.isProcessed ? 'Synced' : 'Not Synced'}
              </Button>
            </Td>
            <Td>
              <IconButton
                icon={deletingId === item._id ? <Spinner size="sm" /> : <FaTrash />}
                colorScheme="red"
                aria-label="Delete"
                mr={2}
                isDisabled={deletingId !== null || !item.isChunked}
                onClick={() => handleDelete(item._id)}
                title={
                  !item.isChunked
                    ? "Can't delete unchunked inventory"
                    : 'Delete inventory'
                }
              />
              <IconButton
                icon={downloadingId === item._id ? <Spinner size="sm" /> : <FaDownload />}
                colorScheme="blue"
                aria-label="Download"
                isDisabled={downloadingId !== null}
                onClick={() => handleDownload(item._id, item.fileName)}
              />
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
};

export default InventoriesTable;
